import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Box, Heading, Text } from '@99designs/emu';
import { mediaQuery } from '@99designs/emu/css';
import { spaces } from '@99designs/emu/settings';
import { IconDown1 } from '@99designs/icons';
import { publicImageUrl } from '../../../lib/publicImageUrl';
import { DWB21_Cream } from './colors';

const Wrapper = styled(Box)<{ navHeight: number }>`
    max-width: 1920px;
    min-height: calc(100vh - ${({ navHeight }) => navHeight}px);
    margin: ${spaces.auto};
    background: ${DWB21_Cream};
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    overflow: hidden;
`;

const ContentWrapper = styled(Box)`
    max-width: 540px;
    display: flex;
    gap: ${spaces['1bu']};
    flex-direction: column;
    margin-top: -6%;

    ${mediaQuery.only('medium')} {
        max-width: 480px;
    }
    ${mediaQuery.only('small')} {
        max-width: 320px;
        margin-top: -15%;
    }
`;

const StyledHeading = styled(Heading)`
    text-align: center;
    font-size: 76px;
    ${mediaQuery.between('small', 'medium')} {
        font-size: 56px;
    }
`;

const StyledText = styled(Text)`
    font-size: 18px;
    ${mediaQuery.between('small', 'medium')} {
        font-size: 16px;
    }
`;

const ScrollIndicatorWrapper = styled.div`
    position: absolute;
    bottom: ${spaces['1.5bu']};

    ${mediaQuery.between('small', 'medium')} {
        bottom: ${spaces['4bu']};
    }
`;

const DesktopScrollIndicator = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: ${spaces['0.25bu']};
    ${mediaQuery.between('small', 'medium')} {
        display: none;
    }
`;

const ImageContainer = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;

    img {
        position: absolute;
        overflow: hidden;

        width: 100px;
        ${mediaQuery.minimum('medium')} {
            width: 200px;
        }

        :nth-child(1) {
            top: 5%;
            left: 0%;
            ${mediaQuery.minimum('normal')} {
                top: 20%;
                left: 0%;
            }
        }
        :nth-child(2) {
            top: 74%;
            left: 5%;
            ${mediaQuery.minimum('normal')} {
                top: 50%;
                left: 10%;
            }
            ${mediaQuery.between('normal', 'large')} {
                top: 65%;
            }
        }
        :nth-child(3) {
            top: 10%;
            left: 20%;
            ${mediaQuery.between('small', 'medium')} {
                display: none;
            }
        }
        :nth-child(4) {
            top: 82%;
            left: 32%;
            ${mediaQuery.between('small', 'medium')} {
                display: none;
            }
            ${mediaQuery.between('normal', 'large')} {
                display: none;
            }
        }
        :nth-child(5) {
            top: 2%;
            left: 65%;
            ${mediaQuery.between('small', 'medium')} {
                display: none;
            }
        }
        :nth-child(6) {
            top: 0%;
            left: 55%;
            width: 160px;
            ${mediaQuery.minimum('medium')} {
                width: 300px;
            }
            ${mediaQuery.minimum('normal')} {
                top: 20%;
                left: 78%;
            }
        }
        :nth-child(7) {
            top: 65%;
            left: 60%;
            width: 200px;
            ${mediaQuery.minimum('medium')} {
                width: 350px;
            }
            ${mediaQuery.minimum('normal')} {
                top: 48%;
                left: 78%;
            }
        }
    }
`;

const backgroundImageUrls = [
    'design-without-borders/Intro_Illus_Pencil.gif',
    'design-without-borders/Intro_Illus_Lightbulb.gif',
    'design-without-borders/Intro_Illus_LieDown.gif',
    'design-without-borders/Intro_Illus_Clock.gif',
    'design-without-borders/Intro_Illus_Laptop.gif',
    'design-without-borders/Intro_Illus_SitPhone.gif',
    'design-without-borders/Intro_Illus_Climbing.gif',
];

interface IntroProps {
    navHeight: number;
    elementToScrollTo: HTMLDivElement | null;
}
export default function Intro({ navHeight, elementToScrollTo }: IntroProps) {
    const [showScrollIndicator, setShowScrollIndicator] = useState(true);
    useEffect(() => {
        const handleScroll = () => {
            // Make scroll indicator disappear when scrolling, to make page more dynamic
            setShowScrollIndicator(false);
            // Add indicator back when it goes back to the top
            if (window.scrollY === 0) {
                setShowScrollIndicator(true);
            }
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [navHeight]);

    function handleScrollClick() {
        if (elementToScrollTo) {
            const { offsetTop } = elementToScrollTo;
            window.scrollTo({
                top: offsetTop - navHeight,
                behavior: 'smooth',
            });
        }
    }

    return (
        <Box background={DWB21_Cream}>
            <Wrapper navHeight={navHeight}>
                <ContentWrapper>
                    <StyledHeading accessibilityLevel={1} appearanceLevel={0}>
                        A fresh <br />
                        perspective <br />
                        on freelance
                    </StyledHeading>
                    <StyledText align="center" as="p">
                        10,000+ designers. 144 countries. New industry perspectives. See the world
                        through the eyes of creatives, with insights and opinions from freelancers
                        around the world.
                    </StyledText>
                </ContentWrapper>
                {showScrollIndicator && (
                    <ScrollIndicatorWrapper onClick={handleScrollClick}>
                        <DesktopScrollIndicator>
                            <Text fontFamily="attribution" italic>
                                Scroll
                            </Text>
                            <IconDown1
                                cursor="pointer"
                                height={spaces['2bu']}
                                width={spaces['2bu']}
                            ></IconDown1>
                        </DesktopScrollIndicator>
                    </ScrollIndicatorWrapper>
                )}

                <ImageContainer>
                    {backgroundImageUrls.map((imageUrl, index) => {
                        // eslint-disable-next-line @next/next/no-img-element
                        return <img key={index} src={publicImageUrl(imageUrl)} alt="" />;
                    })}
                </ImageContainer>
            </Wrapper>
        </Box>
    );
}
